import ReactiveDialog from "../common/ReactiveDialog";
import { useEffect, useState } from "react";
import {
  useCreateTaskInspiration,
  useUpdateTaskInspiration,
  useCreateTaskInspirationCategory,
} from "services/tasks";

import { EphemeralTaskInspiration, TaskFormErrors } from "../../types/tasks";
import { TaskInspiration, TaskInspirationCategory } from "protogen/tasks_pb";
import { AgentPrompt } from "protogen/agents_service_pb";
import TaskInspirationForm from "./TaskInspirationForm";

type Props = {
  open: boolean;
  taskInspiration: EphemeralTaskInspiration | null;
  taskInspirationCategories: TaskInspirationCategory[];
  playbooks: AgentPrompt[];
  onClose: () => void;
  onCreate?: (s?: TaskInspiration) => void;
  onEdit?: (s?: TaskInspiration) => void;
};

const defaultFormData = (): EphemeralTaskInspiration => ({
  title: "",
  status: "active",
  categoryRef: "",
  defaultDetails: [],
});

const validateTaskInspiration = (
  t: EphemeralTaskInspiration,
): TaskFormErrors => {
  const errors: TaskFormErrors = {};
  if (!t.title) {
    errors.title = "Title is required";
  }
  if (t.defaultDetails?.some((d) => !(d.name && d.type))) {
    errors.details = "Details require a name and type";
  }
  return errors;
};

export default ({
  open,
  taskInspiration,
  taskInspirationCategories,
  playbooks,
  onClose,
  onCreate,
  onEdit,
}: Props) => {
  const [formErrors, setFormErrors] = useState<TaskFormErrors | null>(null);
  const [formData, setFormData] =
    useState<EphemeralTaskInspiration>(defaultFormData());

  const { request: createRequest } = useCreateTaskInspiration((r) => {
    onCreate && onCreate(r?.taskInspiration);
  });
  const { request: editRequest } = useUpdateTaskInspiration((r) => {
    onEdit && onEdit(r?.taskInspiration);
  });
  const { request: createCategoryRequest } = useCreateTaskInspirationCategory();

  useEffect(() => {
    if (!open) {
      setFormData(defaultFormData());
    } else if (taskInspiration !== null) {
      setFormData({
        ref: taskInspiration.ref,
        categoryRef: taskInspiration.categoryRef,
        title: taskInspiration.title,
        status: taskInspiration.status,
        additionalContext: taskInspiration.additionalContext,
        defaultDetails: taskInspiration.defaultDetails,
        playbookRef: taskInspiration.playbookRef,
      });
    }
  }, [open]);

  const updateFormData = (s: EphemeralTaskInspiration) => {
    setFormData((prevState) => ({
      ...(prevState || {}),
      ...s,
    }));
  };
  const _validate = () => {
    const errors = validateTaskInspiration(formData);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSave = async (
    isNewCategory: boolean,
    catgoryTitle: string,
    categoryDescription: string,
  ) => {
    if (_validate()) {
      let categoryResp;

      if (taskInspiration !== null) {
        await editRequest({
          taskInspirationRef: formData.ref!,
          title: formData.title || "",
          status: formData.status || "active",
          additionalContext: formData.additionalContext || "",
          defaultDetails: formData.defaultDetails || [],
          playbookRef: formData.playbookRef || "",
        });
      } else {
        if (isNewCategory) {
          categoryResp = await createCategoryRequest({
            title: catgoryTitle,
            description: categoryDescription,
            iconName: "",
          });
        }
        await createRequest({
          title: formData.title || "",
          categoryRef: isNewCategory
            ? categoryResp?.taskInspirationCategory?.ref || ""
            : formData.categoryRef || "",
          additionalContext: formData.additionalContext || "",
          defaultDetails: formData.defaultDetails || [],
          playbookRef: formData.playbookRef || "",
        });
      }
      onClose();
    }
  };
  return (
    <ReactiveDialog
      title={
        taskInspiration !== null
          ? "Edit task inspiration"
          : "Add task inspiration"
      }
      fullWidthSize="sm"
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      <TaskInspirationForm
        formData={formData}
        taskInspirationCategories={taskInspirationCategories}
        playbooks={playbooks}
        updateFormData={updateFormData}
        onSave={onSave}
        isEdit={taskInspiration !== null}
        errors={formErrors}
      ></TaskInspirationForm>
    </ReactiveDialog>
  );
};
